/* eslint-disable local/ensure-wac-token-usage */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { injectIntl, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import {
	ufoEventConfirmationPageTemplateSelectionBack,
	ufoEventConfirmationPageTemplateSelectionContinue,
} from '@/components/signup/utils/ufo-events.ts';
import { wacToken } from '@/libraries/design/foundations';
import {
	TRACKING_EVENT_ACTION_SUBJECT_IDS,
	USER_SEARCH_PARAMS,
} from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts';

import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import { useGupsStatus } from '../../hooks/use-gups.tsx';
import {
	JiraTemplateChoice,
	useJiraTemplateChoice,
} from '../../hooks/use-jira-template-choice.tsx';
import { useScreenManager } from '../../hooks/use-screen-manager';
import { UserSegmentationTeamTypes } from '../../models/user-segmentation';
import { getCurrentUrlWithParams } from '../../utils/utils';
import { JiraOnboardingContainer } from '../../views/jira/jira-onboarding-container/jira-onboarding-container';

import Body from './components/body';
import { getTeamTypeTemplateOptions } from './team-type-template-map';
import { TemplateOption, TemplateOptionMetadata } from './template-definitions';

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '32px',
		lineHeight: '38px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	p: {
		font: token('font.body'),
		color: token('color.text.subtle'),
		margin: '8px 0 32px',
	},
});

const buttonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

const backButtonCss = css({
	outline: `1px solid ${token('color.border')}`,
	outlineOffset: '-1px',
	borderRadius: '6px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		color: `${token('color.text')} !important`,
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

const buttonWrapperStyleDefault = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	margin: 0,
	marginBlockStart: token('space.300'),
	padding: 0,
	border: 'none',
	gap: token('space.100'),
	width: '100%',
	[media.below.sm]: {
		position: 'fixed',
		insetInlineStart: 0,
		insetBlockEnd: '0',
		backgroundColor: '#FFF',
		padding: '14px 18px',
		width: '100%',
		zIndex: 1000,
		boxShadow: '-1px 0px 2px 0px #00000040',
	},
});

const getMessages = (
	formatMessage: (parameters: { id: string; defaultMessage: string }) => string,
) => ({
	header: formatMessage({
		id: 'TransparentTemplateChoice.header.latest',
		defaultMessage: 'Select a template to get started',
	}),
	subheader: formatMessage({
		id: 'TransparentTemplateChoice.subheader.latest',
		defaultMessage: "Your choice won't limit what you can do in Jira",
	}),
	back: formatMessage({
		id: 'TransparentTemplateChoice.backButton',
		defaultMessage: 'Back',
	}),
	continue: formatMessage({
		id: 'TransparentTemplateChoice.continueButton',
		defaultMessage: 'Continue',
	}),
	transparentTemplateV2subHeader: formatMessage({
		id: 'TransparentTemplateV2.subheader',
		defaultMessage:
			'You can always change this later. Selecting a template won’t limit what you can do.',
	}),
});

export const TemplateChoiceViewBase = ({
	contextSelectedTeamTypeState,
}: {
	contextSelectedTeamTypeState: UserSegmentationTeamTypes;
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addTemplateCallback } = useGupsStatus();

	//set these on submit
	const {
		setTemplateChoice,
		setTemplateChoiceId,
		setTemplateChoiceName,
		goBackToUserSegmentation,
		setTemplateMetadata,
	} = useJiraTemplateChoice();

	const { prevScreen, nextScreen, prevScreenExists } = useScreenManager();

	const {
		userSegmentationContext: { selectedTeamType: rawSelectedTeamType },
	} = useConfirmationPage();

	const selectedTeamType = contextSelectedTeamTypeState;

	const doesPreviousScreenExist = prevScreenExists && prevScreenExists();

	const removeUrlParameter = useCallback((url: string, paramKey: string) => {
		const r = new URL(url);
		r.searchParams.delete(paramKey);
		return r.href;
	}, []);

	//set these on select
	const [selectedTemplate, setSelectedTemplate] = useState<JiraTemplateChoice>();
	const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
	const [selectedCardTitle, setSelectedCardTitle] = useState<string>();
	const [selectedTemplateMetadata, setSelectedTemplateMetadata] =
		useState<TemplateOptionMetadata>();

	// get template choices based on team type
	const teamTemplateChoices: TemplateOption[] = useMemo(
		() => getTeamTypeTemplateOptions(selectedTeamType, intl.formatMessage),
		[intl.formatMessage, selectedTeamType],
	);

	useLayoutEffect(() => {
		// Ensure team type param is set in the URL before experiment exposure.
		// This allows for proper enrollment into the experiment for existing Jira users
		// that do not set their team type on the previous screen and
		// instead, it is resolved from a fetch request to their ID profile.
		const url = new URL(window.location.href);
		if (!rawSelectedTeamType) {
			return;
		}
		if (!url.searchParams.get('team_type')) {
			url.searchParams.set('team_type', rawSelectedTeamType);
			history.replaceState(null, '', url.toString());
		}
	}, [rawSelectedTeamType]);

	//set selected template to first template in array
	useEffect(() => {
		if (teamTemplateChoices) {
			setSelectedTemplateId(teamTemplateChoices[0]?.option.templateId);
			setSelectedTemplate(teamTemplateChoices[0]?.option.templateType);
			setSelectedTemplateMetadata(teamTemplateChoices[0]?.metadata);
			setSelectedCardTitle(teamTemplateChoices[0]?.option.title);
		}
	}, [teamTemplateChoices]);

	const handleBack = useCallback(() => {
		try {
			ufoEventConfirmationPageTemplateSelectionBack.start();
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceBack,
				attributes: {
					atlasTicket: 'ATLAS-69623',
				},
			}).fire();
			const updatedUrl = removeUrlParameter(window.location.href, USER_SEARCH_PARAMS.teamType);
			window.history.replaceState({}, '', updatedUrl);
			goBackToUserSegmentation();
			prevScreen();
			ufoEventConfirmationPageTemplateSelectionBack.success();
		} catch (e) {
			ufoEventConfirmationPageTemplateSelectionBack.failure({
				error: e,
			});
		}
	}, [createAnalyticsEvent, goBackToUserSegmentation, prevScreen, removeUrlParameter]);

	const handleContinue = useCallback(async () => {
		try {
			ufoEventConfirmationPageTemplateSelectionContinue.start();
			ufoEventConfirmationPageTemplateSelectionContinue.setCustomData({
				teamType: selectedTeamType,
				template: selectedTemplate,
				templateId: selectedTemplateId,
			});
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceStartNow,
				attributes: {
					atlasTicket: 'ATLAS-69623',
					template: selectedTemplate,
					teamType: selectedTeamType,
					templateId: selectedTemplateId,
				},
			}).fire();

			// add templateChoice to query param to maintain selection on refresh
			const updatedUrl = getCurrentUrlWithParams({
				templateId: selectedTemplateId || '',
				templateChoice: selectedTemplate || '',
				jtbd: selectedTemplateMetadata?.jtbd || '',
			});
			window.history.replaceState({}, '', updatedUrl);

			if (selectedTemplateId) {
				await addTemplateCallback(selectedTemplateId);
			}

			setTemplateMetadata(selectedTemplateMetadata);
			setTemplateChoice(selectedTemplate);
			setTemplateChoiceId(selectedTemplateId);
			setTemplateChoiceName(selectedCardTitle);

			nextScreen();
			ufoEventConfirmationPageTemplateSelectionContinue.success();
		} catch (e) {
			ufoEventConfirmationPageTemplateSelectionContinue.failure({
				error: e,
			});
		}
	}, [
		addTemplateCallback,
		createAnalyticsEvent,
		nextScreen,
		selectedCardTitle,
		selectedTeamType,
		selectedTemplate,
		selectedTemplateId,
		selectedTemplateMetadata,
		setTemplateChoice,
		setTemplateChoiceId,
		setTemplateChoiceName,
		setTemplateMetadata,
	]);

	const messages = useMemo(() => getMessages(intl.formatMessage), [intl.formatMessage]);

	return (
		<div css={titleCss}>
			<JiraOnboardingContainer
				header={messages.header}
				subheader={messages.transparentTemplateV2subHeader}
				hideFooterOnMobile={true}
			>
				<Body
					setSelectedTemplate={setSelectedTemplate}
					setSelectedTemplateId={setSelectedTemplateId}
					setSelectedCardTitle={setSelectedCardTitle}
					setSelectedTemplateMetadata={setSelectedTemplateMetadata}
					selectedCardTitle={selectedCardTitle}
					cards={teamTemplateChoices}
					teamType={selectedTeamType}
				/>
				<div css={buttonWrapperStyleDefault}>
					{doesPreviousScreenExist && (
						<div css={backButtonCss}>
							<Button shouldFitContainer appearance="subtle" onClick={handleBack}>
								{messages.back}
							</Button>
						</div>
					)}
					<div css={buttonCss}>
						<Button shouldFitContainer appearance="primary" onClick={handleContinue}>
							{messages.continue}
						</Button>
					</div>
				</div>
			</JiraOnboardingContainer>
		</div>
	);
};

export const TemplateChoiceView = injectIntl(() => {
	//screen analytics event here
	const {
		userSegmentationContext: {
			selectedTeamType: contextSelectedTeamTypeState = 'software-development',
		},
	} = useConfirmationPage();

	return <TemplateChoiceViewBase contextSelectedTeamTypeState={contextSelectedTeamTypeState} />;
}) as unknown as React.ComponentType<object>;
